/**
 * Pure callback-parsing helpers for the ACI OAuth landing screen.
 *
 * Kept out of `aci-oauth-landing.tsx` so that file exports a component and
 * nothing else: a module that mixes a component with plain helpers defeats React
 * Fast Refresh for every consumer of those helpers
 * (react-doctor/only-export-components).
 */
export type AciOAuthCompletion =
  | { kind: 'success'; ownerId: string }
  | { kind: 'cancelled' }
  | { kind: 'error'; errorCode: string }

export const resolveOAuthCallbackSource = (
  search: string,
  currentAciEnabled: boolean,
): 'aci' | 'nango' => {
  const params = new URLSearchParams(search)
  if (params.has('org_app_uuid')) return 'nango'
  if (params.has('oauth_status') || params.has('oauth_error') || params.has('owner_id'))
    return 'aci'
  return currentAciEnabled ? 'aci' : 'nango'
}

export const parseAciOAuthCompletion = (search: string): AciOAuthCompletion => {
  const params = new URLSearchParams(search)
  const status = params.get('oauth_status')
  const errorCode = params.get('oauth_error')

  if (status === 'success') {
    const ownerId = params.get('owner_id')
    return ownerId ? { kind: 'success', ownerId } : { kind: 'error', errorCode: 'missing_owner_id' }
  }

  if (status === 'oauth_cancelled' || status === 'cancelled' || errorCode === 'oauth_cancelled') {
    return { kind: 'cancelled' }
  }

  return {
    kind: 'error',
    errorCode: errorCode || status || 'missing_status',
  }
}

/** Reserved TLD (RFC 2606), so no real host can ever collide with it. */
const RETURN_TO_BASE = 'https://return-to.invalid'

/**
 * Resolves the stored return path and keeps only the relative part, so the value
 * can never carry a host of its own. A string prefix check is not enough: the
 * URL parser folds backslashes into slashes for special schemes, so `/\evil.com`
 * becomes `//evil.com` and re-hosts, which `navigate()` then throws on.
 *
 * Resolution uses a fixed sentinel base rather than `window.location.origin`,
 * which is the opaque string "null" in an `about:blank` or sandboxed document
 * and would make every return path collapse to `/home`.
 */
export const safeReturnTo = (storedPath: string | null) => {
  if (!storedPath) return '/home'
  try {
    const resolved = new URL(storedPath, RETURN_TO_BASE)
    if (resolved.origin !== RETURN_TO_BASE) return '/home'
    return `${resolved.pathname}${resolved.search}${resolved.hash}`
  } catch {
    return '/home'
  }
}
